import { inject, Injectable } from '@angular/core';
import { _, TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';
import {
  LdActionPlace,
  LdActionType,
  LdCoordinatorDispatch,
} from '@app/main/leak-detection/services/leak-detection-coordinator/leak-detection-coordinator.actions';
import { ldContextMenuTodo } from '@app/main/leak-detection/context-menus/utils/ld-context-menu-todo';
import { IEvent } from '@app/api/interfaces/event.interface';
import { IMainTableCellContext } from '@app/main/main-content/main-table/interfaces/main-table-cell-context.interface';
import { ClipboardCopyService } from '@app/core/services/clipboard-copy/clipboard-copy.service';
import { FormattingDataService } from '@app/core/services/formatting-data/formatting-data.service';
import { ContextMenuCopyService } from '@app/main/main-content/context-menu/services/context-menu-copy.service';
import { LdContextMenuEventsService } from '@app/main/leak-detection/context-menus/services/ld-context-menu-events.service';

@Injectable({ providedIn: 'root' })
export class LdContextMenuEventService {
  private readonly translateService = inject(TranslateService);
  private readonly formattingData = inject(FormattingDataService);
  private readonly clipboardCopy = inject(ClipboardCopyService);
  private readonly contextMenuCopy = inject(ContextMenuCopyService);
  private readonly contextMenuEvents = inject(LdContextMenuEventsService);

  public getMapItems(event: IEvent, dispatch: LdCoordinatorDispatch): MenuItem[] {
    return this.getCommonItems(event, LdActionPlace.Map, dispatch);
  }

  public getTableItems(
    event: IEvent,
    cell: IMainTableCellContext | undefined,
    dispatch: LdCoordinatorDispatch
  ): MenuItem[] {
    return [
      ...this.contextMenuCopy.getCopyCellItems(cell),
      ...this.getCommonItems(event, LdActionPlace.TabsPanel, dispatch),
    ];
  }

  private getCommonItems(event: IEvent, place: LdActionPlace, dispatch: LdCoordinatorDispatch): MenuItem[] {
    // an event carries no coordinates until it is located, so both coordinate entries stay disabled
    const coordinates = event.coordinates;

    return [
      {
        label: this.translateService.instant(_(`@@leak-detection-context-menu-event-details__label`)),
        icon: 'icons/icon-event-details.svg',
        command: () => dispatch({ type: LdActionType.EventDbClick, place, item: event }),
      },
      {
        // the right-clicked event is the related one here, so the entry is always actionable
        ...this.contextMenuEvents.getRemoveFromEventConfig(event),
        command: () => ldContextMenuTodo('Remove from event', event),
      },
      { separator: true },
      {
        label: this.translateService.instant(_(`@@leak-detection-context-menu-copy-coordinates__label`)),
        icon: 'icons/icon-copy-big.svg',
        disabled: !coordinates,
        command: () => this.clipboardCopy.copy(this.formattingData.formatCoordinates(coordinates)),
      },
      {
        label: this.translateService.instant(_(`@@leak-detection-context-menu-show-in-street-view__label`)),
        icon: 'icons/icon-show-on-street.svg',
        iconStyle: { 'original-icon': true },
        disabled: !coordinates,
        command: () => coordinates && dispatch({ type: LdActionType.ShowInStreetView, place, position: coordinates }),
      },
    ];
  }
}
